import React, { Fragment, useEffect, useState } from "react";
import styles from "../topology.module.scss";
import {
  Form,
  Select,
  Button,
  Row,
  Col,
  Divider,
  Popover,
  message,
  Tooltip,
  Space,
  FormInstance,
  notification,
} from "antd";
import { useTranslation } from "react-i18next";
import {
  setDeviceNum,
  setSelectedModel,
  setImportSource,
  setSaveConfigVisible,
  setDeviceIdToGetConfig,
  setFileNameToGetConfig,
  setFormData,
  setResultModalVisible,
  setResultList,
  setIsConfigFetching,
} from "../store/topologySlice";
import { useAppSelector, useAppDispatch } from "store/hook";
import { useRepositoryList } from "Components/DeviceBackup/queries";
import { useDeviceStatus } from "Components/Dashboard/queries";
import { useDeviceConfig, useGetConfigByFile, useDeviceReboot, useUpdateEventLog } from "Components/Topology/queries";
import { deviceNameToID } from "./utils";
import { useFilter } from "Utility/hooks/useFilter";
import { TableData } from "Utility/Types/status_identity_get_set";
import { FilterOutlined } from "@ant-design/icons";

interface FilterProps {
  SubmitConfigonFinish: (arg: any) => void;
  form: FormInstance<any>;
  submitSettingLoading: boolean;
}

const { Option } = Select;

const initialFilter: Record<string, string[]> = {
  model: [],
  gid: [],
  city: [],
  health: ["up", "warning", "critical"],
};

const BulkConfigFilter = React.memo(({ SubmitConfigonFinish, form, submitSettingLoading }: FilterProps) => {
  const [filterVisible, setFilterVisible] = useState(false);
  const { t } = useTranslation();
  const [DeviceSearch, setDeviceSearch] = useState<string | undefined>("any");
  const [isFilterFormChange, setIsFilterFormChange] = useState(false);
  const { deviceNum, importSource, deviceIdToGetConfig, fileNameToGetConfig } = useAppSelector(
    (state) => state.topology
  );
  const { edittingInterface } = useAppSelector((state) => state.setting);
  const { level, loginCid, stateCid, user } = useAppSelector((state) => state.global);
  const disp = useAppDispatch();
  const isAllCollapseClosed = edittingInterface.length; //判斷是否有任一setting開啟? 沒有就disable相關按鈕
  const { isFetching: repositoryListFetching, data: repositoryList } = useRepositoryList(level, stateCid, loginCid);
  const { isFetching: deviceStatusFetching, data: deviceStatus } = useDeviceStatus(level, stateCid, loginCid);
  const { isFetching: deviceConfigFetching, data: deviceConfig } = useDeviceConfig(deviceIdToGetConfig); //device id改變, 獲取config
  const { isFetching: deviceConfigByFileFetching, data: deviceConfigByFile } = useGetConfigByFile(
    level,
    stateCid,
    loginCid,
    fileNameToGetConfig
  );

  const fetching =
    deviceConfigFetching ||
    repositoryListFetching ||
    deviceStatusFetching ||
    deviceConfigByFileFetching ||
    submitSettingLoading;

  useEffect(() => {
    disp(setIsConfigFetching(fetching));
  }, [fetching]);

  const selectedModel = Form.useWatch("model", form);
  const { mutateAsync: deviceRebootMutation } = useDeviceReboot();
  const forbiddenLevel = level === "get" || (level === "super_super" && stateCid === "");
  const { mutateAsync: setEventLogMutation } = useUpdateEventLog();
  const [filter, setFilter] = useState(initialFilter);
  const [tableData, setTableData] = useState<TableData[]>([]);
  const [importDevice_modelMap, setImportDevice_modelMap] = useState<any>();
  const [tableData_ImportDevice, setTableData_ImportDevice] = useState<TableData[]>([]);
  const [restoreData] = useFilter({
    filter,
    tableData: deviceStatus?.response?.tableData || [],
    setDeviceTableData: setTableData,
  });

  // 新增 deviceStatus 依賴項，排除 tableData_ImportDevice undefined 的問題 #3/5
  useEffect(() => {
    const deviceData: any = [];
    const deviceModelMap: any = {};

    deviceStatus?.response?.tableData?.map((item: any) => {
      if (item.health !== "offline") {
        deviceData.push(item);
        // TODO: 修正設定 裝置 -> 點選 裝置下拉式選單無資料問題會，導致往後端送出 Device reboot or Setting no reseponse #2/27
        // 原本用 item.name_id 會導致 deviceModelMap 無法匹配到相對應的 Model
        deviceModelMap[item.id] = item.model;
      }
    });
    setTableData_ImportDevice(deviceData || []);
    setImportDevice_modelMap(deviceModelMap || {});
  }, [deviceStatus]);

  useEffect(() => {
    form.resetFields();
    setFilter(JSON.parse(JSON.stringify(initialFilter)));
  }, [stateCid]);

  useEffect(() => {
    let config: any;

    if (importSource === "Device" || (deviceConfig && !deviceConfigByFile)) {
      // M350 有些機型無 wifi，但設備回傳 wifi => delete wifi property (mantis 2119)
      if (deviceConfig?.obj?.wifi?.wifi_config?.ap_enable === null) delete deviceConfig.obj.wifi;

      config = deviceConfig;
      if (!config) return;
      //set model field based on import data
      form.setFieldsValue({
        importSource: "Device",
        model: config.nodeInf.model,
        deviceID: config.nodeInf.name_or_id, //import source : single device
        devicesID_or_Name: [config.nodeInf.name_or_id], //devices to be set: multiple
      });
    } else if (importSource === "FileRepository") {
      config = deviceConfigByFile;
      if (!config) return;

      // To get the last device name based on ID (in case device ID has changed)
      config.nodeInf.name_or_id = [...tableData]
        .filter((device) => config.nodeInf.id.includes(device.id))
        .map((device) => {
          return device.name_or_id;
        });

      //set model field based on import file
      form.setFieldsValue({
        importSource: "FileRepository",
        model: config.nodeInf.model,
        deviceID: config.nodeInf.name_or_id, //import source : single device
        devicesID_or_Name: config.nodeInf.name_or_id, //devices to be set: multiple
      });
    } else {
      return;
    }

    // if device no response: reset all field
    if (config?.obj === "No Response!") {
      form?.resetFields();
      disp(setImportSource("NewConfig"));
      message.error("No Response");
      return;
    }
    // 從裝置點擊 Device 設定icon 跳轉 設定頁面時在篩選一次型號 #3/1
    const seletedModel = config.nodeInf.model;
    setFilter({ ...filter, model: [seletedModel] });

    disp(setSelectedModel(config.nodeInf.model)); //store current selected model (different model has it own interface form)
    disp(setFormData(config.obj)); //store config.object as global variable
  }, [deviceConfig, deviceConfigByFile, importSource]);

  useEffect(() => {
    disp(setDeviceNum(form.getFieldValue("devicesID_or_Name") || []));
  }, [form.getFieldValue("devicesID_or_Name")]);

  const handleVisibleChange = (visible: boolean) => {
    setFilterVisible(visible);
    if (isFilterFormChange) {
      form.resetFields([["devicesID_or_Name"]]);
    }
    setIsFilterFormChange(false);
  };
  function SelectAll() {
    const allDevice = tableData?.map((item: any) => (item.name !== "" ? item.name : item.id));
    form.setFieldsValue({
      devicesID_or_Name: allDevice,
    });
    disp(setDeviceNum(allDevice));
  }

  function ClearAll() {
    form.setFieldsValue({
      devicesID_or_Name: [],
    });
    disp(setDeviceNum([]));
  }

  const rebootDevices = () => {
    let deviceID = form.getFieldsValue().devicesID_or_Name || [];

    deviceID = deviceNameToID(tableData, deviceID);

    deviceRebootMutation(deviceID)
      .then((res) => {
        disp(setResultModalVisible(true));
        disp(
          setResultList({
            failureDevices: [],
            rebootDevices: [],
            errorDevices: [],
            successDevices: [],
            ...res,
          })
        );
        const isSuprt = level === "super_super";
        const eventmessage = `${user} reboot device *${res.total}`;
        const cid = isSuprt ? stateCid : loginCid;
        setEventLogMutation({ cid, message: eventmessage });
      })
      .catch(() => {
        message.error(t("ISMS.Fail"));
      });
  };

  const content = (
    <Form layout="vertical">
      <div className="formwrap" style={{ width: "20rem", padding: "5px" }}>
        <Col xs={24} sm={24} md={24} lg={24} xl={24}>
          <Form.Item label={t("ISMS.Group")} name="gid">
            <Select
              loading={fetching}
              showSearch
              showArrow
              disabled={fetching}
              mode="multiple"
              maxTagCount={"responsive"}
              placeholder={t("ISMS.Select")}
              onChange={(value: string[]) => {
                setFilter({ ...filter, gid: value });
              }}
            >
              {deviceStatus?.response?.groups?.map((item: any, index: number) => {
                return (
                  <Option key={index} value={item}>
                    {item}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={24} lg={24} xl={24}>
          <Form.Item label={t("ISMS.Location")} name="city">
            <Select
              loading={fetching}
              showSearch
              showArrow
              disabled={fetching}
              mode="multiple"
              maxTagCount={"responsive"}
              placeholder={t("ISMS.Select")}
              onChange={(value: string[]) => {
                setFilter({ ...filter, city: value });
              }}
            >
              {deviceStatus?.response?.locations?.map((item: any, index: number) => {
                return (
                  <Option key={index} value={item}>
                    {item}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>
        </Col>
      </div>
    </Form>
  );

  const fieldPanelMap: Record<string, string> = {
    LAN: "LAN",
    WAN: "WAN",
    LTE: "LTE",
    WiFi: "WiFi",
    Alarm: "Alarm",
    Period: "Period",
  };
  // 沒有前綴的欄位清單
  const customFields: Record<string, string> = {
    // Filter
    model: "Model",
    devicesID_or_Name: "Device",
    // LAN
    LAN_ipv4_address: "LAN_IPv4_IP Address",
    LAN_ipv4_netmask: "LAN_IPv4_IP Mask",
    LAN_ipv4_dhcp_start: "LAN_IPv4_DHCP_IP Address Pool (From)",
    LAN_ipv4_dhcp_end: "LAN_IPv4_DHCP_IP Address Pool (To)",
    StaticIP: "LAN_IPv4_DHCP_StaticIP",
    ipv6_WAN_static_adress: "LAN_IPv6_WAN_Static Address",
    ipv6_WAN_prefix_length: "LAN_IPv6_WAN_Prefix Length",
    ipv6_WAN_default_gateway: "LAN_IPv6_WAN_Default Gateway",
    ipv6_LAN_static_adress: "LAN_IPv6_LAN_Static Address",
    ipv6_LAN_prefix_length: "LAN_IPv6_LAN_Prefix Length",
    ipv6_DNS_server_1: "LAN_IPv6_DNS_Server 1",
    ipv6_DNS_server_2: "LAN_IPv6_DNS_Server 2",
    LAN_ipv6_adress: "LAN_IPv6_Static Address",
    // WAN
    active_profile: "WAN_Profile",
    profile_name_1: "WAN_Profile Name 1",
    profile_name_2: "WAN_Profile Name 2",
    profile_name_3: "WAN_Profile Name 3",
    WAN_username: "WAN_PPPoE_User Name",
    WAN_static_ipv4_address: "WAN_Static_IPv4_IP Address",
    WAN_static_netmask: "WAN_Static_IPv4_IP Mask",
    WAN_static_gateway: "WAN_Static_IPv4_Gateway",
    Table1: "WAN_Profile TABLE 1",
    Table2: "WAN_Profile TABLE 2",
    Table3: "WAN_Profile TABLE 3",
    WAN_priority_1: "WAN_Priority_1st",
    WAN_priority_2: "WAN_Priority_2nd",
    WAN_priority_3: "WAN_Priority_3th",
    WAN_static_ipv4_dns_address1: "WAN_Static_IPv4 DNS Server #1",
    WAN_ipv4_address_1: "WAN_DHCP_IPv4_Address #1",
    WAN_ipv4_address_2: "WAN_DHCP_IPv4_Address #2",
    WAN_ipv4_address_3: "WAN_DHCP_IPv4_Address #3",
    WAN_lte_mode: "WAN_Priority_LTE Mode",
    // LTE
    LTE_connect_retry_num: "LTE_Connect Retry Number",
    LTE_mtu: "LTE_MTU",
    LTE_recovery_down_time: "LTE_Recover DownTimes",
    LTE1_limit_mbyte: "LTE_SIM1_Limit_Max Data Limitation (MB)",
    LTE1_reset_day: "LTE_SIM1_Limit_Reset Date",
    LTE1_reset_hour: "LTE_SIM1_Limit_Hour",
    LTE1_reset_minute: "LTE_SIM1_Limit_Minute",
    LTE1_reset_second: "LTE_SIM1_Limit_Second",
    LTE2_limit_mbyte: "LTE_SIM2_Limit_Max Data Limitation (MB)",
    LTE2_reset_day: "LTE_SIM2_Limit_Reset Date",
    LTE2_reset_hour: "LTE_SIM2_Limit_Hour",
    LTE2_reset_minute: "LTE_SIM2_Limit_Minute",
    LTE2_reset_second: "LTE_SIM2_Limit_Second",
    // WiFi
    Wifi_ap_mode: "WiFi_WiFi AP Mode",
    Wifi1_txpower: "WiFi_Tx Power",
    Wifi1_CountryCode: "WiFi_Country Code",
    Wifi1_channel_id: "WiFi_Channel",
    Wifi1_ssid: "WiFi_Name(SSID)",
    Wifi1_passphrase: "WiFi_Passphrase",
    Wifi1_rekey: "WiFi_Key Update",
    Wifi1_vlan: "WiFi_VLAN Subnet",
    Wifi2_CountryCode: "WiFi_2-Country Code",
    Wifi2_channel_id: "WiFi_SSID-2_Channel",
    Wifi2_ssid: "WiFi_SSID-2_Name(SSID)",
    Wifi2_passphrase: "WiFi_SSID-2_Passphrase",
    Wifi2_rekey: "WiFi_SSID-2_Key Update",
    Wifi2_vlan: "WiFi_SSID-2_VLAN Subnet",
    Wifi_5g_txpower: "WiFi_5G_Tx Power",
    Wifi_5g_CountryCode: "WiFi_5G_Country Code",
    Wifi1_5g_channel_id: "WiFi_5G_Channel",
    Wifi1_5g_ssid: "WiFi_5G_Name(SSID)",
    Wifi1_5g_passphrase: "WiFi_5G_Passphrase",
    Wifi1_5g_rekey: "WiFi_5G_Key Update",
    Wifi1_5g_vlan: "WiFi_5G_VLAN Subnet",
    Wifi2_5g_channel_id: "WiFi_5G_SSID_SSID-2_Channel",
    Wifi2_5g_ssid: "WiFi_5G_SSID-2_Name(SSID)",
    Wifi2_5g_passphrase: "WiFi_5G_SSID-2_Passphrase",
    Wifi2_5g_rekey: "WiFi_5G_SSID-2_Key Update",
    Wifi2_5g_vlan: "WiFi_5G_SSID-2_VLAN Subnet",
    // Alarm
    alarm_input: "Alarm_Type",
    gps_radius: "Alarm_GPS_Radius(m)",
    gps_latitude: "Alarm_GPS_Latitude",
    gps_longitude: "Alarm_GPS_Longitude",
    cpu_low: "Alarm_CPU_Low (°C)",
    cpu_high: "Alarm_CPU_High (°C)",
    // Period
    alive: "Period_Alive Period (step by 30)",
    status: "Period_Status Period",
    iot: "Period_IoT Period",
    gps: "Period_GPS Period",
  };

  function getFieldLabel(name: string | string[]): string {
    const key = Array.isArray(name) ? name.join("_") : name;

    // 先檢查是否在自定義清單
    if (customFields[key]) return customFields[key];

    // 嘗試從 Panel 映射找前綴
    for (const panelKey in fieldPanelMap) {
      if (key.toLowerCase().startsWith(panelKey.toLowerCase())) {
        const labelPart = key.slice(panelKey.length).replace(/_/g, " ");
        return `${fieldPanelMap[panelKey]}: ${labelPart}`;
      }
    }

    // 沒有前綴就直接返回原名
    return key;
  }

  const handleJumpToFirstError = (errorInfo: any) => {
    if (!errorInfo?.errorFields?.length) return;

    const firstError = errorInfo.errorFields[0];
    const name = Array.isArray(firstError.name) ? firstError.name[0] : firstError.name;

    // scroll 到欄位
    form.scrollToField(name, { behavior: "smooth", block: "center" });

    // 嘗試 focus，如果欄位已渲染的話
    setTimeout(() => {
      const fieldNode = form.getFieldInstance(firstError.name);
      if (fieldNode?.focus) fieldNode.focus();
    }, 100);
  };

  const SubmitConfigonFinishFailed = (errorInfo: any) => {
    if (!errorInfo?.errorFields?.length) return;

    // focus 第一個錯誤欄位
    handleJumpToFirstError(errorInfo);

    const errorFields = errorInfo.errorFields.map((f: any) => {
      const name = Array.isArray(f.name) ? f.name[0] : f.name;
      return getFieldLabel(name); // <-- 這裡轉成可讀 label
    });
    const groupedFields: Record<string, string[]> = {};

    // 分組
    errorFields.forEach((field: string) => {
      const prefixMatch = Object.keys(fieldPanelMap).find((prefix) => field.startsWith(prefix));

      const groupKey = prefixMatch || "Filter";
      const cleanLabel = prefixMatch ? field.replace(new RegExp(`^${prefixMatch}[_: ]?`, "i"), "") : field;

      if (!groupedFields[groupKey]) groupedFields[groupKey] = [];
      // 去重：只加入尚未出現的欄位
      if (!groupedFields[groupKey].includes(cleanLabel)) {
        groupedFields[groupKey].push(cleanLabel);
      }
    });

    // 生成 notification 描述，每個 panel 分成多行
    const description = Object.entries(groupedFields).map(([panel, fields], i) => (
      <div key={i}>
        <b>{fieldPanelMap[panel] || panel}:</b>
        <div style={{ paddingLeft: 12 }}>
          {fields.map((f, j) => (
            <div key={j}>- {f}</div>
          ))}
        </div>
      </div>
    ));

    notification.error({
      message: t("ISMS.Fields Required"),
      description: description.map((d, i) => <div key={i}>{d}</div>),
      key: "unique_error_notification",
      duration: 5,
      placement: "bottomRight",
      className: styles.FieldsNotification,
    });
  };

  return (
    <Form onFinish={SubmitConfigonFinish} form={form} layout="vertical" scrollToFirstError>
      <div className="formwrap">
        <Row gutter={24} justify="start">
          <Col xs={24} sm={24} md={24} lg={4}>
            <Form.Item
              name="importSource"
              label={t("ISMS.Config Source")}
              initialValue={"NewConfig"}
              tooltip="Import setting from device or file"
            >
              <Select
                placeholder={t("ISMS.Select")}
                onChange={(value: "Device" | "NewConfig" | "FileRepository") => {
                  form.resetFields();
                  form.setFieldsValue({ importSource: value });
                  disp(setImportSource(value));
                  disp(setDeviceIdToGetConfig(undefined));
                  disp(setFileNameToGetConfig(undefined));
                  setFilter(initialFilter);
                }}
                disabled={fetching}
              >
                <Option value={"NewConfig"}> {t("ISMS.New Config")}</Option>
                <Option value={"Device"}> {t("ISMS.Device")}</Option>
                <Option value={"FileRepository"}> {t("ISMS.FileRepository")}</Option>
              </Select>
            </Form.Item>
          </Col>

          {(importSource === "Device" || (deviceConfig && !deviceConfigByFile)) && (
            <Col xs={24} sm={24} md={24} lg={7}>
              <Form.Item
                name="deviceID"
                label={t("ISMS.Import Device")}
                tooltip='"UP" device only'
                rules={[{ required: true, message: "Device is required" }]}
              >
                <Select
                  placeholder={t("ISMS.Select")}
                  onChange={(value: string) => {
                    const model = importDevice_modelMap[`${value}`];
                    disp(setDeviceIdToGetConfig(JSON.parse(JSON.stringify(value))));
                    disp(setSelectedModel(model));
                    disp(setDeviceNum([]));
                    setFilter({ ...filter, model: [model] });
                  }}
                  disabled={fetching}
                  loading={fetching}
                  showSearch
                  optionFilterProp="children"
                >
                  {tableData_ImportDevice
                    ?.filter((item: any) => item.health !== "offline")
                    .map((item: any, index: number) => {
                      return (
                        <Option key={index} value={item.id}>
                          {item.name_or_id}
                        </Option>
                      );
                    })}
                </Select>
              </Form.Item>
            </Col>
          )}

          {importSource === "FileRepository" && (
            <Col xs={24} sm={24} md={24} lg={7}>
              <Form.Item
                name="FileRepository"
                label={t("ISMS.Import File")}
                tooltip='"YML" file only'
                rules={[{ required: true, message: "Device is required" }]}
              >
                <Select
                  placeholder={t("ISMS.Select")}
                  onChange={(value: string) => {
                    disp(setFileNameToGetConfig(value));
                  }}
                  loading={fetching}
                  disabled={fetching || level === "get"}
                >
                  {repositoryList?.[0]?.list
                    ?.filter((file: any) => file.type === "yml")
                    ?.map((item: any, index: number) => {
                      return (
                        <Option value={item.name} key={index}>
                          {item.name}
                        </Option>
                      );
                    })}
                </Select>
              </Form.Item>
            </Col>
          )}

          <Col xs={24} sm={24} md={24} lg={10} xl={4}>
            <Form.Item
              label={t("ISMS.Model")}
              name="model"
              rules={
                importSource !== "NewConfig"
                  ? [{ required: false }]
                  : [{ required: true, message: "Model is required" }]
              }
            >
              <Select
                loading={fetching}
                showSearch
                showArrow
                disabled={fetching || importSource !== "NewConfig"}
                placeholder={t("ISMS.Select")}
                onChange={(value: string) => {
                  disp(setSelectedModel(value));
                  disp(setDeviceNum([]));
                  form.setFieldValue("devicesID_or_Name", []);
                  setFilter({ ...filter, model: [value] });
                }}
              >
                {deviceStatus?.response?.models?.map((item: string, index: number) => {
                  return (
                    <Option key={index} value={item}>
                      {item}
                    </Option>
                  );
                })}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={24} lg={10} xl={9}>
            <Form.Item
              label={
                <Fragment>
                  {t("ISMS.Device")}
                  <Popover
                    content={content}
                    trigger="click"
                    visible={filterVisible}
                    onVisibleChange={handleVisibleChange}
                  >
                    <Button size="small" type="text" icon={<FilterOutlined />}></Button>
                  </Popover>
                </Fragment>
              }
              tooltip="Devices to be set"
              name="devicesID_or_Name"
              rules={[{ required: true, message: "Deivce is required!" }]}
            >
              <Select
                autoClearSearchValue={false}
                loading={fetching}
                disabled={fetching || selectedModel === undefined}
                mode="multiple"
                placeholder={t("ISMS.Select")}
                showArrow
                maxTagCount={"responsive"}
                onChange={(value: string[]) => disp(setDeviceNum(value))}
                onBlur={() => setDeviceSearch("any")}
                onSearch={(value) => {
                  if (value === "") {
                    setDeviceSearch("any");
                  } else {
                    setDeviceSearch(undefined);
                  }
                }}
                dropdownRender={(menu) => (
                  <Fragment>
                    {menu}
                    {DeviceSearch && (
                      <>
                        <Divider className="my-2" />
                        <Space className="p-1">
                          <Button size="small" onClick={() => SelectAll()}>
                            {t("ISMS.SelectAll")}
                          </Button>
                          <Button size="small" onClick={() => ClearAll()}>
                            {t("ISMS.ClearAll")}
                          </Button>
                        </Space>
                      </>
                    )}
                  </Fragment>
                )}
              >
                {tableData?.map((item: any, index: number) => {
                  return (
                    <Option key={index} value={item.name !== "" ? item.name : item.id}>
                      {item.name !== "" ? item.name : item.id}
                    </Option>
                  );
                })}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24} justify="space-between" style={{ padding: "0 12px" }}>
          <Space>
            <Form.Item>
              <Tooltip placement="topLeft" title={forbiddenLevel ? "level: super_super/get not allowed" : ""}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={fetching}
                  disabled={!isAllCollapseClosed || forbiddenLevel || deviceNum.length === 0}
                >
                  {t("ISMS.Submit")}
                </Button>
              </Tooltip>
            </Form.Item>
            <Form.Item>
              <Tooltip placement="topLeft" title={forbiddenLevel ? "level: super_super/get not allowed" : ""}>
                <Button
                  type="primary"
                  danger
                  onClick={() => {
                    rebootDevices();
                  }}
                  loading={fetching}
                  disabled={forbiddenLevel || deviceNum.length === 0}
                >
                  {t("ISMS.Reboot")}
                </Button>
              </Tooltip>
            </Form.Item>
          </Space>

          <Space>
            <Form.Item>
              <Button
                loading={fetching}
                onClick={() => {
                  form.resetFields();
                  disp(setDeviceIdToGetConfig(undefined));
                  disp(setFileNameToGetConfig(undefined));
                  disp(setDeviceNum([]));
                  disp(setImportSource("NewConfig"));
                  disp(setFormData([]));
                  setFilter(initialFilter);
                }}
              >
                {t("ISMS.Clear")}
              </Button>
            </Form.Item>
            <Form.Item>
              <Button
                loading={fetching}
                onClick={async () => {
                  try {
                    await form.validateFields();
                    disp(setSaveConfigVisible(true));
                  } catch (errorInfo) {
                    SubmitConfigonFinishFailed(errorInfo);
                  }
                }}
                disabled={!isAllCollapseClosed || level === "get"}
              >
                {t("ISMS.Save")}
              </Button>
            </Form.Item>
          </Space>
        </Row>
      </div>
    </Form>
  );
});

export default BulkConfigFilter;

// export const setConfigDataToForm = (form: any, config: any, device_or_file: string, tableData: any[]) => {
//   return (disp: any) => {
//     //若為NoResponse 則停止所有動作
//     if (config.obj === "No Response!") {
//       form?.resetFields();
//       disp(setImportSource("NewConfig"));
//       message.error("No Response");
//       return;
//     }

//     //避免file存檔後, device被rename, 導致import到舊的device name => 強迫從id中重新尋找device name
//     if (device_or_file === "FileRepository") {
//       let nodeNameList = tableData
//         .filter((device) => config.nodeInf.id.includes(device.id))
//         .map((device) => {
//           return device.name_or_id;
//         });
//       config.nodeInf.name = nodeNameList;
//     }

//     form.setFieldsValue({
//       model: config.nodeInf.model,
//       devicesID_or_Name: device_or_file === "Device" ? [config.nodeInf.name_or_id] : config.nodeInf.name, //devices欄位為多選, 轉換為陣列
//     });

//     disp(setSelectedModel(config.nodeInf.model)); //紀錄當前選擇的model, 表單差異由此判斷
//     disp(setFormData(config.obj)); //紀錄obj內容, 用來setForm value
//     // disp(setImportSource(device_or_file)); //紀錄import行為, 是否移除disp??
//     // disp(setDeviceIdToGetConfig(undefined)); //還原監控參數, 是否需要??
//     // disp(setFileNameToGetConfig(undefined));
//     // disp(
//     //   setShowSettingItems({
//     //     LAN: false,
//     //     WAN: false,
//     //     LTE: false,
//     //     WiFi: false,
//     //     Alarm: false,
//     //     Period: false,
//     //   })
//     // ); //預設所有設定欄位關閉
//   };
// };
